/**
 * Neue Buchungs-Kategorie – eigenständige Tab-Seite (Buchhaltung)
 */
import { useState } from 'react';
import { Tag, Check, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useTabs } from '@/contexts/TabContext';
import { emitDataRefresh } from '@/lib/tabEvents';
import { toast } from 'sonner';

const BACKEND_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

export default function NeueKategoriePage() {
  const { activeTab, closeTab } = useTabs();

  const [form, setForm] = useState({ name: '', typ: 'neutral', massnahme_typ: '', keywords: '' });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.name) { toast.error('Bitte Name angeben'); return; }
    setSaving(true);
    try {
      const keywords = form.keywords.split(',').map(k => k.trim().toLowerCase()).filter(Boolean);
      await fetch(`${BACKEND_URL}/api/buchhaltung/kategorien`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: form.name.toLowerCase().replace(/\s+/g, '_').replace(/[äöü]/g, c => ({ä:'ae',ö:'oe',ü:'ue'}[c])),
          name: form.name, typ: form.typ,
          massnahme_typ: form.massnahme_typ || null,
          keywords
        })
      });
      toast.success('Kategorie erstellt');
      emitDataRefresh('buchhaltung');
      closeTab(activeTab.id);
    } catch { toast.error('Fehler'); } finally { setSaving(false); }
  };

  return (
    <div className="max-w-lg mx-auto" data-testid="neue-kategorie-page">
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Tag className="w-5 h-5 text-orange-600" />Neue Kategorie erstellen
            </CardTitle>
            <Button variant="ghost" size="icon" onClick={() => closeTab(activeTab.id)}><X className="w-4 h-4" /></Button>
          </div>
          <CardDescription>Kategorien werden für die automatische Zuordnung von Transaktionen verwendet</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Name</Label>
            <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
              placeholder="z.B. Physiotherapie" data-testid="kategorie-name-input" />
          </div>
          <div>
            <Label>Typ</Label>
            <Select value={form.typ} onValueChange={v => setForm(p => ({ ...p, typ: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="einnahme">Einnahme</SelectItem>
                <SelectItem value="ausgabe">Ausgabe</SelectItem>
                <SelectItem value="neutral">Neutral</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Massnahmen-Typ (optional)</Label>
            <Input value={form.massnahme_typ} onChange={e => setForm(p => ({ ...p, massnahme_typ: e.target.value }))}
              placeholder="z.B. physiotherapie, grundsicherung" />
            <p className="text-xs text-slate-400 mt-1">Für automatische Verknüpfung mit Massnahmen</p>
          </div>
          <div>
            <Label>Keywords für Auto-Kategorisierung</Label>
            <Input value={form.keywords} onChange={e => setForm(p => ({ ...p, keywords: e.target.value }))}
              placeholder="praxis, therapie, behandlung" />
            <p className="text-xs text-slate-400 mt-1">Komma-getrennt. Werden für automatische Zuordnung verwendet.</p>
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" onClick={() => closeTab(activeTab.id)}>Abbrechen</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-kategorie">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Wird erstellt...</> : <><Check className="w-4 h-4 mr-2" />Erstellen</>}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
